import React from 'react';
import { MessageCircle, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * WhatsApp Button Component
 * Displays floating WhatsApp chat and call buttons
 * 
 * @param {string} phoneNumber - Phone number for WhatsApp chat (defaults to USA number)
 * @param {string} callNumber - Phone number for call (defaults to India number)
 * @param {string} message - Pre-filled message for WhatsApp chat (optional)
 * @param {string} position - Position of buttons: 'bottom-right' (default) or 'bottom-left'
 */
const WhatsAppButton = ({ 
  phoneNumber, 
  callNumber,
  message = "Hello, I'd like to know more about your dental services.",
  position = 'bottom-right'
}) => {
  // Default numbers: USA for WhatsApp, India for call
  const defaultWhatsAppNumber = '+16238248761'; // USA number
  const defaultCallNumber = '+919629996774'; // India number
  
  // Use provided numbers or defaults
  const whatsappNum = phoneNumber || defaultWhatsAppNumber;
  const callNum = callNumber || defaultCallNumber;
  
  // Format phone number for WhatsApp: remove any non-digit characters
  let cleanWhatsAppPhone = whatsappNum.replace(/\D/g, '');
  
  // Remove leading 0 if present
  if (cleanWhatsAppPhone.startsWith('0')) {
    cleanWhatsAppPhone = cleanWhatsAppPhone.substring(1);
  }
  
  // Remove duplicate country codes if present
  if (cleanWhatsAppPhone.startsWith('9191')) {
    cleanWhatsAppPhone = cleanWhatsAppPhone.substring(2);
  }
  if (cleanWhatsAppPhone.startsWith('11')) {
    cleanWhatsAppPhone = cleanWhatsAppPhone.substring(1); // Remove duplicate 1
  }
  
  // Format WhatsApp number (USA)
  const whatsappLength = cleanWhatsAppPhone.length;
  const whatsappStartsWith1 = cleanWhatsAppPhone.startsWith('1');
  
  if (whatsappStartsWith1 && whatsappLength === 11) {
    // Already has US country code (1) - use as-is
    cleanWhatsAppPhone = cleanWhatsAppPhone;
  } else if (whatsappLength === 10) {
    // 10-digit number - add US country code
    cleanWhatsAppPhone = '1' + cleanWhatsAppPhone;
  } else if (!whatsappStartsWith1 && whatsappLength > 10) {
    // Has country code but not US - assume it's correct
    cleanWhatsAppPhone = cleanWhatsAppPhone;
  } else if (whatsappLength < 10) {
    // Too short - add US country code
    cleanWhatsAppPhone = '1' + cleanWhatsAppPhone;
  }
  
  // Format phone number for call: remove any non-digit characters
  let cleanCallPhone = callNum.replace(/\D/g, '');
  
  // Remove leading 0 if present
  if (cleanCallPhone.startsWith('0')) {
    cleanCallPhone = cleanCallPhone.substring(1);
  }
  
  // Remove duplicate country codes if present
  if (cleanCallPhone.startsWith('9191')) {
    cleanCallPhone = cleanCallPhone.substring(2);
  }
  
  // Format call number (India)
  const callLength = cleanCallPhone.length;
  const callStartsWith91 = cleanCallPhone.startsWith('91');
  
  if (callStartsWith91 && callLength >= 12) {
    // Already has India country code (91) - use as-is
    cleanCallPhone = cleanCallPhone;
  } else if (callLength === 10) {
    // 10-digit number - add India country code
    cleanCallPhone = '91' + cleanCallPhone;
  } else if (!callStartsWith91 && callLength > 10) {
    // Has country code but not India - assume it's correct
    cleanCallPhone = cleanCallPhone;
  } else if (callLength < 10) {
    // Too short - add India country code
    cleanCallPhone = '91' + cleanCallPhone;
  }
  
  // WhatsApp URL format: https://wa.me/911234567890 (without +)
  // WhatsApp chat URL (USA number)
  const whatsappChatUrl = `https://wa.me/${cleanWhatsAppPhone}?text=${encodeURIComponent(message)}`;
  
  // Regular call URL (India number) - use formatted number with + for tel: links
  const telUrl = `tel:+${cleanCallPhone}`;

  const positionClasses = position === 'bottom-left' 
    ? 'bottom-6 left-6' 
    : 'bottom-6 right-6';

  return (
    <div className={`fixed ${positionClasses} z-50 flex flex-col gap-3`}>
      {/* WhatsApp Chat Button */}
      <motion.a
        href={whatsappChatUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center w-14 h-14"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </motion.a>

      {/* WhatsApp Call Button */}
      <motion.a
        href={telUrl}
        className="bg-[#128C7E] hover:bg-[#0F7A6E] text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center w-14 h-14"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Call us"
        title="Call us"
      >
        <Phone className="h-6 w-6" />
      </motion.a>
    </div>
  );
};

export default WhatsAppButton;
